// Blog2List.js - Contains functions for creating a new blog listing section
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a Blog List section (Layout 2) with a featured post and a list of other posts.
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the blog section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The blog section elements
 */
export const createBlog2Elements = (
  getHierarchyName,
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const sectionClass = 'blog2-section';
  const containerClass = 'blog2-container';
  const mainTitleClass = 'blog2-main-title';

  const featuredPostClass = 'blog2-featured-post';
  const featuredImageWrapperClass = 'blog2-featured-image-wrapper';
  const featuredImageClass = 'blog2-featured-image';
  const featuredContentClass = 'blog2-featured-content';
  const featuredCategoryClass = 'blog2-featured-category';
  const featuredTitleClass = 'blog2-featured-title';
  const featuredExcerptClass = 'blog2-featured-excerpt';
  const featuredMetaClass = 'blog2-featured-meta';
  const featuredAuthorClass = 'blog2-featured-author';
  const featuredDateClass = 'blog2-featured-date';

  const postsListClass = 'blog2-posts-list';
  const postItemClass = 'blog2-post-item';
  const postItemImageClass = 'blog2-post-item-image';
  const postItemContentClass = 'blog2-post-item-content';
  const postItemCategoryClass = 'blog2-post-item-category';
  const postItemTitleClass = 'blog2-post-item-title';
  const postItemDateClass = 'blog2-post-item-date';

  const loadMoreButtonClass = 'blog2-load-more-button';

  // --- Styles ---

  // Desktop Styles
  const sectionStyles = {
    position: 'relative',
    padding: '80px 20px',
    backgroundColor: '#f9f9f9',
    width: '100%',
  };

  const containerStyles = {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '50px',
  };

  const mainTitleStyles = {
    fontSize: '40px',
    fontWeight: '700',
    color: '#2c3e50',
    textAlign: 'center',
    marginBottom: '20px',
  };

  const featuredPostStyles = {
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
    overflow: 'hidden',
    gap: '0px',
  };

  const featuredImageWrapperStyles = {
    flex: '1 1 50%',
    minHeight: '400px',
    position: 'relative',
  };

  const featuredImageStyles = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  };

  const featuredContentStyles = {
    flex: '1 1 50%',
    padding: '40px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '15px',
  };

  const featuredCategoryStyles = {
    fontSize: '14px',
    fontWeight: '600',
    color: '#e74c3c',
    textTransform: 'uppercase',
  };

  const featuredTitleStyles = {
    fontSize: '32px',
    fontWeight: '700',
    color: '#2c3e50',
    lineHeight: '1.3',
  };

  const featuredExcerptStyles = {
    fontSize: '16px',
    color: '#555555',
    lineHeight: '1.7',
  };

  const featuredMetaStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '14px',
    color: '#7f8c8d',
    marginTop: '20px',
  };

  const featuredAuthorStyles = {
    fontWeight: '500',
  };

  const featuredDateStyles = {
    fontStyle: 'italic',
  };

  const postsListStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '30px',
  };

  const postItemStyles = {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 4px 15px rgba(0,0,0,0.06)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  };

  const postItemImageStyles = {
    width: '100%',
    height: '180px',
    objectFit: 'cover',
  };

  const postItemContentStyles = {
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    flexGrow: '1',
  };

  const postItemCategoryStyles = {
    fontSize: '12px',
    fontWeight: '600',
    color: '#3498db',
    textTransform: 'uppercase',
  };

  const postItemTitleStyles = {
    fontSize: '20px',
    fontWeight: '600',
    color: '#333333',
    lineHeight: '1.4',
    flexGrow: '1',
  };

  const postItemDateStyles = {
    fontSize: '13px',
    color: '#888888',
    marginTop: '10px',
    textAlign: 'right',
  };

  const loadMoreButtonStyles = {
    alignSelf: 'center',
    padding: '15px 30px',
    backgroundColor: '#2ecc71',
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '600',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease, transform 0.2s ease',
    marginTop: '30px',
    boxShadow: '0 4px 10px rgba(46, 204, 113, 0.3)',
  };
  
  // Tablet Styles
  const tabletFeaturedPostStyles = {
    flexDirection: 'column',
  };
  const tabletFeaturedImageWrapperStyles = {
    minHeight: '300px',
  };
  const tabletFeaturedContentStyles = {
    padding: '30px',
  };

  // Mobile Styles
  const mobileMainTitleStyles = {
    fontSize: '32px',
  };
  const mobileFeaturedContentStyles = {
    padding: '25px',
  };
  const mobileFeaturedTitleStyles = {
    fontSize: '26px',
  };
  const mobilePostsListStyles = {
    gridTemplateColumns: '1fr',
  };
   const mobileLoadMoreButtonStyles = {
    width: '100%',
    padding: '15px 20px',
  };

  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };

    newRegistry[DEVICES.DESKTOP] = newRegistry[DEVICES.DESKTOP] || new Map();
    newRegistry[DEVICES.DESKTOP].set(sectionClass, sectionStyles);
    newRegistry[DEVICES.DESKTOP].set(containerClass, containerStyles);
    newRegistry[DEVICES.DESKTOP].set(mainTitleClass, mainTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(featuredPostClass, featuredPostStyles);
    newRegistry[DEVICES.DESKTOP].set(featuredImageWrapperClass, featuredImageWrapperStyles);
    newRegistry[DEVICES.DESKTOP].set(featuredImageClass, featuredImageStyles);
    newRegistry[DEVICES.DESKTOP].set(featuredContentClass, featuredContentStyles);
    newRegistry[DEVICES.DESKTOP].set(featuredCategoryClass, featuredCategoryStyles);
    newRegistry[DEVICES.DESKTOP].set(featuredTitleClass, featuredTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(featuredExcerptClass, featuredExcerptStyles);
    newRegistry[DEVICES.DESKTOP].set(featuredMetaClass, featuredMetaStyles);
    newRegistry[DEVICES.DESKTOP].set(featuredAuthorClass, featuredAuthorStyles);
    newRegistry[DEVICES.DESKTOP].set(featuredDateClass, featuredDateStyles);
    newRegistry[DEVICES.DESKTOP].set(postsListClass, postsListStyles);
    newRegistry[DEVICES.DESKTOP].set(postItemClass, postItemStyles);
    newRegistry[DEVICES.DESKTOP].set(postItemImageClass, postItemImageStyles);
    newRegistry[DEVICES.DESKTOP].set(postItemContentClass, postItemContentStyles);
    newRegistry[DEVICES.DESKTOP].set(postItemCategoryClass, postItemCategoryStyles);
    newRegistry[DEVICES.DESKTOP].set(postItemTitleClass, postItemTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(postItemDateClass, postItemDateStyles);
    newRegistry[DEVICES.DESKTOP].set(loadMoreButtonClass, loadMoreButtonStyles);

    newRegistry[DEVICES.TABLET] = newRegistry[DEVICES.TABLET] || new Map();
    newRegistry[DEVICES.TABLET].set(featuredPostClass, tabletFeaturedPostStyles);
    newRegistry[DEVICES.TABLET].set(featuredImageWrapperClass, tabletFeaturedImageWrapperStyles);
    newRegistry[DEVICES.TABLET].set(featuredContentClass, tabletFeaturedContentStyles);

    newRegistry[DEVICES.MOBILE] = newRegistry[DEVICES.MOBILE] || new Map();
    newRegistry[DEVICES.MOBILE].set(mainTitleClass, mobileMainTitleStyles);
    newRegistry[DEVICES.MOBILE].set(featuredPostClass, tabletFeaturedPostStyles); 
    newRegistry[DEVICES.MOBILE].set(featuredImageWrapperClass, {...tabletFeaturedImageWrapperStyles, minHeight: '250px'});
    newRegistry[DEVICES.MOBILE].set(featuredContentClass, mobileFeaturedContentStyles);
    newRegistry[DEVICES.MOBILE].set(featuredTitleClass, mobileFeaturedTitleStyles);
    newRegistry[DEVICES.MOBILE].set(postsListClass, mobilePostsListStyles);
    newRegistry[DEVICES.MOBILE].set(loadMoreButtonClass, mobileLoadMoreButtonStyles);

    return newRegistry;
  });

  setGlobalClassNames(prev => new Set([
    ...prev,
    sectionClass, containerClass, mainTitleClass,
    featuredPostClass, featuredImageWrapperClass, featuredImageClass, featuredContentClass,
    featuredCategoryClass, featuredTitleClass, featuredExcerptClass, featuredMetaClass,
    featuredAuthorClass, featuredDateClass,
    postsListClass, postItemClass, postItemImageClass, postItemContentClass,
    postItemCategoryClass, postItemTitleClass, postItemDateClass,
    loadMoreButtonClass
  ]));

  const generateId = () => uuidv4();

  const sectionId = generateId();
  const containerId = generateId();
  const mainTitleId = generateId();

  const featuredPostId = generateId();
  const featuredImageWrapperId = generateId();
  const featuredImageId = generateId();
  const featuredContentId = generateId();
  const featuredCategoryId = generateId();
  const featuredTitleId = generateId();
  const featuredExcerptId = generateId();
  const featuredMetaId = generateId();
  const featuredAuthorId = generateId();
  const featuredDateId = generateId();

  const postsListId = generateId();
  const loadMoreButtonId = generateId();

  const createPostItem = (index) => {
    const postId = generateId();
    const imageId = generateId();
    const contentId = generateId();
    const categoryId = generateId();
    const titleId = generateId();
    const dateId = generateId();

    const postTitles = ["Exploring New Horizons in Tech", "The Art of Minimalist Design", "Sustainable Living: A Guide"];
    const postCategories = ["Technology", "Design", "Lifestyle"];
    const postDates = ["June 10, 2024", "June 12, 2024", "June 15, 2024"];
    const postImages = [
        'https://images.unsplash.com/photo-1518770660439-4636190af475?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=60',
        'https://images.unsplash.com/photo-1558655146-d09347e92766?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=60',
        'https://images.unsplash.com/photo-1491975474562-1f4e30bc9468?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=60'
    ];

    return [
      {
        id: postId, 
        builderId: uuidv4(), 
        type: 'div', 
        name: `Blog Post Item ${index + 1}`, 
        parentId: postsListId, 
        hierarchyName: getHierarchyName('div'),
        className: postItemClass, 
        styles: {}, 
        children: [imageId, contentId], 
        text: '', 
        attributes: { 'data-element': 'blog-post-item' }
      },
      {
        id: imageId, 
        builderId: uuidv4(), 
        type: 'image', 
        name: `Post Item Image ${index + 1}`, 
        parentId: postId, 
        hierarchyName: getHierarchyName('image'),
        className: postItemImageClass, 
        styles: {}, 
        src: postImages[index % postImages.length], 
        attributes: { 'data-element': 'blog-post-item-image', 
        alt: postTitles[index % postTitles.length] }
      },
      {
        id: contentId, 
        builderId: uuidv4(), 
        type: 'div', 
        name: `Post Item Content ${index + 1}`, 
        parentId: postId, hierarchyName: getHierarchyName('div'),
        className: postItemContentClass, 
        styles: {}, 
        children: [categoryId, titleId, dateId], 
        text: '', 
        attributes: { 'data-element': 'blog-post-item-content' }
      },
      {
        id: categoryId, 
        builderId: uuidv4(), 
        type: 'div', 
        name: `Post Item Category ${index + 1}`,
        parentId: contentId, 
        hierarchyName: getHierarchyName('div'),
        className: postItemCategoryClass, 
        styles: {}, 
        children: [], 
        text: postCategories[index % postCategories.length], 
        attributes: { 'data-element': 'blog-post-item-category' }
      },
      {
        id: titleId, 
        builderId: uuidv4(), 
        type: 'h4', 
        name: `Post Item Title ${index + 1}`, 
        parentId: contentId, 
        hierarchyName: getHierarchyName('h4'),
        className: postItemTitleClass, 
        styles: {}, 
        children: [], 
        text: postTitles[index % postTitles.length], 
        attributes: { 'data-element': 'blog-post-item-title' }
      },
      {
        id: dateId, 
        builderId: uuidv4(), 
        type: 'div', 
        name: `Post Item Date ${index + 1}`, 
        parentId: contentId, 
        hierarchyName: getHierarchyName('div'),
        className: postItemDateClass, 
        styles: {}, 
        children: [], 
        text: postDates[index % postDates.length], 
        attributes: { 'data-element': 'blog-post-item-date' }
      }
    ];
  };

  const postItemsData = Array.from({ length: 3 }, (_, i) => createPostItem(i)).flat();
  const postItemElementIds = postItemsData.filter(el => el.className === postItemClass).map(el => el.id);

  const newElements = [
    {
      id: sectionId, 
      builderId: uuidv4(), 
      type: 'section', 
      name: 'Blog Section Layout 2', 
      parentId: null,
      className: sectionClass, 
      styles: {}, 
      children: [containerId], 
      text: '',
      attributes: { 
        'data-element': 'blog2-section', 
        'data-component': 'section', 
        'data-section-type': 'blog-2', 
        'data-page-id': currentPageId 
    }
    },
    {
      id: containerId, 
      builderId: uuidv4(), 
      type: 'div', 
      name: 'Blog Container 2', 
      parentId: sectionId, 
      hierarchyName: getHierarchyName('div'),
      className: containerClass, 
      styles: {}, 
      children: [mainTitleId, featuredPostId, postsListId, loadMoreButtonId], 
      text: '',
      attributes: { 'data-element': 'blog2-container' }
    },
    {
      id: mainTitleId, 
      builderId: uuidv4(), 
      type: 'h2', 
      name: 'Blog Main Title', 
      parentId: containerId, 
      hierarchyName: getHierarchyName('h2'),
      className: mainTitleClass, 
      styles: {}, 
      children: [], 
      text: 'From Our Creative Minds',
      attributes: { 'data-element': 'blog2-main-title' }
    },
    {
      id: featuredPostId, 
      builderId: uuidv4(), 
      type: 'div', 
      name: 'Featured Blog Post', 
      parentId: containerId, 
      hierarchyName: getHierarchyName('div'),
      className: featuredPostClass, 
      styles: {}, 
      children: [featuredImageWrapperId, featuredContentId], 
      text: '',
      attributes: { 'data-element': 'blog2-featured-post' }
    },
    {
      id: featuredImageWrapperId, 
      builderId: uuidv4(), 
      type: 'div', name: 'Featured Image Wrapper', 
      parentId: featuredPostId, 
      hierarchyName: getHierarchyName('div'),
      className: featuredImageWrapperClass, 
      styles: {}, 
      children: [featuredImageId], 
      text: '',
      attributes: { 'data-element': 'blog2-featured-image-wrapper'}
    },
    {
      id: featuredImageId, 
      builderId: uuidv4(), 
      type: 'image', 
      name: 'Featured Post Image', 
      parentId: featuredImageWrapperId, 
      hierarchyName: getHierarchyName('image'),
      className: featuredImageClass, 
      styles: {}, 
      src: 'https://images.unsplash.com/photo-1501504905252-473c47e087f8?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      attributes: { 'data-element': 'blog2-featured-image', alt: 'Featured Blog Post' }
    },
    {
      id: featuredContentId, 
      builderId: uuidv4(), 
      type: 'div', 
      name: 'Featured Post Content', 
      parentId: featuredPostId, 
      hierarchyName: getHierarchyName('div'),
      className: featuredContentClass, 
      styles: {}, 
      children: [featuredCategoryId, featuredTitleId, featuredExcerptId, featuredMetaId], 
      text: '',
      attributes: { 'data-element': 'blog2-featured-content' }
    },
    {
      id: featuredCategoryId, 
      builderId: uuidv4(), 
      type: 'div', 
      name: 'Featured Post Category', 
      parentId: featuredContentId, 
      hierarchyName: getHierarchyName('div'),
      className: featuredCategoryClass, 
      styles: {}, 
      children: [], 
      text: 'Innovation',
      attributes: { 'data-element': 'blog2-featured-category' }
    },
    {
      id: featuredTitleId, 
      builderId: uuidv4(), 
      type: 'h3', 
      name: 'Featured Post Title', 
      parentId: featuredContentId, 
      hierarchyName: getHierarchyName('h3'),
      className: featuredTitleClass, 
      styles: {}, 
      children: [], 
      text: 'The Future of Web Development: Trends & Insights',
      attributes: { 'data-element': 'blog2-featured-title' }
    },
    {
      id: featuredExcerptId, 
      builderId: uuidv4(), 
      type: 'p', 
      name: 'Featured Post Excerpt', 
      parentId: featuredContentId, 
      hierarchyName: getHierarchyName('p'),
      className: featuredExcerptClass, 
      styles: {}, 
      children: [], 
      text: 'Dive deep into the upcoming trends that will shape the web development landscape. From AI integration to serverless architectures, explore what\'s next.',
      attributes: { 'data-element': 'blog2-featured-excerpt' }
    },
    {
      id: featuredMetaId, 
      builderId: uuidv4(), 
      type: 'div', 
      name: 'Featured Post Meta', 
      parentId: featuredContentId, 
      hierarchyName: getHierarchyName('div'),
      className: featuredMetaClass, 
      styles: {}, 
      children: [featuredAuthorId, featuredDateId], 
      text: '',
      attributes: { 'data-element': 'blog2-featured-meta' }
    },
    {
      id: featuredAuthorId, 
      builderId: uuidv4(), 
      type: 'div', 
      name: 'Featured Post Author', 
      parentId: featuredMetaId, 
      hierarchyName: getHierarchyName('div'),
      className: featuredAuthorClass, 
      styles: {}, children: [], 
      text: 'By Alex Williams',
      attributes: { 'data-element': 'blog2-featured-author' }
    },
    {
      id: featuredDateId, 
      builderId: uuidv4(), 
      type: 'div', 
      name: 'Featured Post Date', 
      parentId: featuredMetaId, 
      hierarchyName: getHierarchyName('div'),
      className: featuredDateClass, 
      styles: {}, 
      children: [], 
      text: 'July 22, 2024',
      attributes: { 'data-element': 'blog2-featured-date' }
    },
    {
      id: postsListId, 
      builderId: uuidv4(), 
      type: 'div', 
      name: 'Blog Posts List', 
      parentId: containerId, 
      hierarchyName: getHierarchyName('div'),
      className: postsListClass, 
      styles: {}, 
      children: postItemElementIds, 
      text: '',
      attributes: { 'data-element': 'blog2-posts-list' }
    },
    ...postItemsData,
    {
      id: loadMoreButtonId, 
      builderId: uuidv4(), 
      type: 'button', 
      name: 'Load More Button', 
      parentId: containerId, 
      hierarchyName: getHierarchyName('button'),
      className: loadMoreButtonClass, 
      styles: {}, 
      children: [], 
      text: 'Load More Articles',
      attributes: { 'data-element': 'blog2-load-more-button' }
    }
  ];

  return {
    blogSection: newElements[0],
    elements: newElements
  };
};

export default createBlog2Elements;